// components/Enrollment/StudentCourseCatalog.js
// Student-facing catalog of batch courses available for self-enrollment
// Uses self-paced card styling for consistency
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import {
  BookOpen, Clock, ChevronRight, Layers, Users, CheckCircle2,
  Search, BookMarked,
} from 'lucide-react';
import { EmptyState, SearchInput, AppBadge, FilterBar } from '../ui';
import { getCatalogCourses } from '../../utils/enrollmentApi';
import '../SelfPaced/_self-paced-pages.scss';

const CatalogCard = ({ course, onClick }) => {
  const feeAmount = parseFloat(String(course.fees || '0').replace(/[^0-9.]/g, '')) || 0;
  const isFree = feeAmount === 0;
  const isEnrolled = course.enrollmentStatus === 'enrolled' || course.enrollmentStatus === 'enrolled_no_batch';
  const isPending = course.enrollmentStatus === 'pending';
  const isApproved = course.enrollmentStatus === 'approved';

  return (
    <div className="spc-card" onClick={onClick} role="button" tabIndex={0}>
      <div className="spc-card__thumb">
        {(course.thumbnailUrl || course.thumbnail || course.image) ? (
          <img src={course.thumbnailUrl || course.thumbnail || course.image} alt={course.name} />
        ) : (
          <div className="spc-card__thumb-placeholder">
            <BookMarked size={32} />
          </div>
        )}
        {course.mode && (
          <span className="spc-card__level spc-card__level--beginner">{course.mode}</span>
        )}
      </div>

      <div className="spc-card__body">
        <h4 className="spc-card__title">{course.name}</h4>
        {course.description && (
          <p className="spc-card__desc">
            {course.description.length > 100
              ? course.description.slice(0, 100) + '…'
              : course.description}
          </p>
        )}
        <div className="spc-card__meta">
          {course.duration && <span><Clock size={13} /> {course.duration}</span>}
          {course.mode && <span><Layers size={13} /> {course.mode}</span>}
          <span><Users size={13} /> {course.batchCount || 0} batch{course.batchCount !== 1 ? 'es' : ''}</span>
        </div>
      </div>

      <div className="spc-card__footer">
        <div className="spc-card__pricing">
          {isEnrolled ? (
            <AppBadge color="emerald" size="sm"><CheckCircle2 size={13} /> Enrolled</AppBadge>
          ) : isPending ? (
            <AppBadge color="warning" size="sm">Pending</AppBadge>
          ) : isApproved ? (
            <AppBadge color="primary" size="sm">Approved</AppBadge>
          ) : isFree ? (
            <span className="spc-card__price spc-card__price--free">Free</span>
          ) : (
            <span className="spc-card__price">₹{feeAmount.toLocaleString('en-IN')}</span>
          )}
        </div>
        <span className="spc-card__action">
          {isEnrolled ? 'View' : 'Details'} <ChevronRight size={14} />
        </span>
      </div>
    </div>
  );
};

export default function StudentCourseCatalog() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await getCatalogCourses();
        setCourses(res.data?.data || []);
      } catch {
        setCourses([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = courses.filter(c =>
    !search || (c.name || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="sp-loading"><Spinner animation="border" variant="secondary" /></div>;
  }

  return (
    <div className="sp-page">
      <div className="spc-hero" style={{ minHeight: 'auto', padding: '1.5rem 0' }}>
        <div className="spc-hero__content">
          <h1 className="spc-hero__title" style={{ fontSize: '1.5rem' }}>Browse Courses</h1>
          <p className="spc-hero__subtitle">Explore available courses and apply to join a batch</p>
        </div>
      </div>

      {courses.length > 0 && (
        <FilterBar
          actions={<SearchInput value={search} onChange={setSearch} placeholder="Search courses…" />}
        >
          <AppBadge variant="secondary">{filtered.length} course{filtered.length !== 1 ? 's' : ''}</AppBadge>
        </FilterBar>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          icon={<Search size={28} />}
          title={search ? 'No courses match your search' : 'No courses available'}
          description={search ? 'Try a different search term.' : 'Check back later for new courses.'}
        />
      ) : (
        <div className="spc-grid">
          {filtered.map(course => (
            <CatalogCard
              key={course._id}
              course={course}
              onClick={() => navigate(`/s/courses/${course._id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
